import {
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import type { MetadataApiProps } from "../../../../features/product/types/report-template";

type AddBlockDialogProps = {
	open: boolean;
	metadata: MetadataApiProps[];
	onClose: () => void;
	onAdd: (apiId: string, responseBlock: string) => void;
};

function fieldCountLabel(fields: unknown): string {
	const count =
		fields && typeof fields === "object" ? Object.keys(fields).length : 0;
	return count === 1 ? "1 campo" : `${count} campos`;
}

export default function AddBlockDialog({
	open,
	metadata,
	onClose,
	onAdd,
}: AddBlockDialogProps) {
	const [api, setApi] = useState<MetadataApiProps | null>(null);
	const [responseBlock, setResponseBlock] = useState("");

	const apis = useMemo(
		() =>
			metadata.filter(
				(item) => item.structure && Object.keys(item.structure).length > 0,
			),
		[metadata],
	);

	function close() {
		setApi(null);
		setResponseBlock("");
		onClose();
	}

	function add() {
		if (!api || !responseBlock) return;
		onAdd(api.id, responseBlock);
		close();
	}

	return (
		<Dialog open={open} onClose={close} fullWidth maxWidth="sm">
			<DialogTitle>Adicionar bloco</DialogTitle>
			<DialogContent
				sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
			>
				<Autocomplete
					options={apis}
					value={api}
					onChange={(_, value) => {
						setApi(value);
						setResponseBlock("");
					}}
					getOptionLabel={(item) => `${item.name} (${item.provider_name})`}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					renderInput={(params) => (
						<TextField
							{...params}
							label="API de origem"
							helperText="Busca por nome da API ou do fornecedor"
							margin="dense"
						/>
					)}
				/>

				{api?.structure && (
					<FormControl>
						<FormLabel id="add-block-response">Bloco de resposta</FormLabel>
						<RadioGroup
							aria-labelledby="add-block-response"
							value={responseBlock}
							onChange={(event) => setResponseBlock(event.target.value)}
						>
							{Object.entries(api.structure).map(([name, fields]) => (
								<FormControlLabel
									key={name}
									value={name}
									control={<Radio size="small" />}
									label={`${name} · ${fieldCountLabel(fields)}`}
								/>
							))}
						</RadioGroup>
					</FormControl>
				)}

				<Typography variant="body2" color="text.secondary">
					O bloco entra no fim da lista, sem campos marcados. Depois é só
					escolher os campos.
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button type="button" onClick={close}>
					Cancelar
				</Button>
				<Button
					type="button"
					variant="contained"
					disabled={!api || !responseBlock}
					onClick={add}
				>
					Adicionar
				</Button>
			</DialogActions>
		</Dialog>
	);
}
